import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import useLocalStorageState from 'use-local-storage-state';
import { STORAGE_KEYS } from '@/containers/CheckoutPage/constants';
import { useIsServerRendered } from '@/containers/CheckoutPage/hooks/useIsServerRendered';
import {
  FormValues,
  formValues,
} from '@/containers/ConfigurePage/components/FormConfig/formData';
import { BASKET_STORAGE_KEY } from '@/containers/ConfigurePage/constants';
import { getSelectedSubscription } from '@/containers/ConfigurePage/utils/getSelectedSubscription';
import { useData } from '@/lib/pageData/pageDataContext';
import { DiscountValidationResponse } from '@/types/checkout/Discount';
import { Enrollment, EnrollmentInfo } from '@/types/checkout/Enrollment';
import { Subscription } from '@/types/checkout/Subscription';
import { CityData } from '@/types/CityData';
import { TransformedAssetCategoryResult } from '@/types/TransformedAssetCategoryResults';
import { SubscriptionType } from '@/utils/fetch/fetchProductSubscriptionByRegion';
import { getCryptoHash } from '@/utils/getCryptoHash';
import { isBrowser } from '@/utils/isBrowser';
import { gtmDataLayerPush } from './gtmDataLayerPush';
import { pushEcommerceNullEvent } from './gtmEvents';

interface CommonProps {
  assetCategoryName: string;
  regionName: string;
  subscription: Subscription;
}

interface CheckoutStep extends CommonProps {
  stepNumber: number | string;
  subscriptionCode: string;
  paymentProvider: string;
}

interface CheckoutSuccess extends CommonProps {
  discount?: DiscountValidationResponse;
  enrollment: EnrollmentInfo;
}

interface ConfigureAddToCart {
  city: CityData;
  product: TransformedAssetCategoryResult;
  subscriptions: SubscriptionType[];
}

export function CheckoutStepGTM({
  assetCategoryName,
  regionName,
  stepNumber,
  subscription,
  subscriptionCode,
  paymentProvider,
}: CheckoutStep) {
  const [tempEnrollment] = useLocalStorageState<Enrollment>(
    STORAGE_KEYS.tempEnrollment
  );
  const [isBasketOptIn] = useLocalStorageState<boolean>(BASKET_STORAGE_KEY);
  const isServerRendered = useIsServerRendered();

  useEffect(() => {
    if (isBrowser && !isServerRendered) {
      const dataLayer = {
        event: 'checkoutStep',
        pageCountry: subscription.countryCode,
        ecommerce: {
          currencyCode: subscription.rates.currencyIsoCode,
          paymentProvider,
          checkout: {
            actionField: { step: stepNumber },
            products: [
              {
                name: assetCategoryName,
                id: subscriptionCode,
                // price is a hardcoded value because it's a required field
                // in GA but we do not use it
                price: 0,
                quantity: 1,
                category: `Bikes/${subscription.assetCategoryCode}`,
                city: regionName,
                membership:
                  subscription.typeDuration.max === 12
                    ? '12months'
                    : subscription.isFlexible
                      ? 'flexible'
                      : 'loyal',
                student: subscription.offers.isStudent ? 'yes' : 'no',
                business: tempEnrollment?.companyName ? 'business' : 'consumer',
                basket: isBasketOptIn ? 'yes' : 'no',
              },
            ],
          },
        },
      };
      pushEcommerceNullEvent();
      gtmDataLayerPush(dataLayer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepNumber, isServerRendered]);

  return null;
}

export function CheckoutSuccessGTM({
  assetCategoryName,
  discount,
  enrollment,
  regionName,
  subscription,
}: CheckoutSuccess) {
  const isServerRendered = useIsServerRendered();
  const [isBasketOptIn] = useLocalStorageState<boolean>(BASKET_STORAGE_KEY);
  const [paymentMethod] = useLocalStorageState<string>(
    STORAGE_KEYS.paymentMethod
  );

  useEffect(
    () => {
      async function generateAndPushWithHash() {
        const shaEmail = await getCryptoHash('SHA-256', enrollment.email);
        const shaPhoneNumber = await getCryptoHash(
          'SHA-256',
          enrollment.telephoneNumber1
        );
        const dataLayer = {
          event: 'purchase',
          pageCountry: subscription.countryCode,
          ecommerce: {
            currencyCode: subscription.rates.currencyIsoCode,
            'sha-em': shaEmail,
            'sha-pn': shaPhoneNumber,
            purchase: {
              actionField: {
                id: enrollment.subscriptionId,
                revenue: 1,
                tax: 0,
                shipping: 0,
              },
              products: [
                {
                  name: assetCategoryName,
                  id: enrollment.subscriptionTypeCode,
                  price: 0,
                  quantity: 1,
                  monthlyPrice: subscription.rates.initial,
                  category: `Bikes/${assetCategoryName}`,
                  city: regionName,
                  membership:
                    subscription.typeDuration.max === 12
                      ? '12months'
                      : subscription.isFlexible
                        ? 'flexible'
                        : 'loyal',
                  student: subscription.offers.isStudent ? 'yes' : 'no',
                  voucherCode: discount?.code,
                  basket: isBasketOptIn ? 'yes' : 'no',
                },
              ],
              paymentMethod,
            },
          },
        };
        pushEcommerceNullEvent();
        gtmDataLayerPush(dataLayer);
      }

      if (isBrowser && !isServerRendered) {
        generateAndPushWithHash();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isServerRendered]
  );

  return null;
}

export const ConfigureAddToCartGTM = ({
  subscriptions,
  city,
  product,
}: ConfigureAddToCart) => {
  const { currency } = useData<'default'>();
  const { getValues } = useFormContext<FormValues>();
  const isServerRendered = useIsServerRendered();
  const [isBasketOptIn] = useLocalStorageState<boolean>(BASKET_STORAGE_KEY);

  const currentFormValues = getValues();
  const subscription = getSelectedSubscription({
    currentFormValues,
    product,
    subscriptions,
  });
  const { assetCategoryName } = product.productConfig;

  useEffect(
    () => {
      if (isBrowser && subscription) {
        const dataLayer = {
          event: 'addToCart',
          ecommerce: {
            currencyCode: currency,
            add: {
              products: [
                {
                  name: assetCategoryName,
                  id: subscription.code,
                  price: subscription.rate,
                  quantity: 1,
                  monthlyPrice: subscription.rate,
                  category: `Bikes/${assetCategoryName}`,
                  city: city.code,
                  membership:
                    currentFormValues.durationMonths === '12'
                      ? '12months'
                      : currentFormValues.durationMonths === '6'
                        ? 'loyal'
                        : 'flexible',
                  student: currentFormValues.student,
                  heavyUse:
                    currentFormValues.heavyUse ?? formValues.heavyUse.no,
                  basket: isBasketOptIn ? 'yes' : 'no',
                },
              ],
            },
          },
        };

        pushEcommerceNullEvent();
        gtmDataLayerPush(dataLayer);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isServerRendered]
  );

  return null;
};
